// apps/admin — single-file React front-end. Auth gate (sign in / create account — the
// account is Chris's, locked by the same first-registration rule as the kids) over core's
// /api endpoints, then the authenticated shell. The dashboard is EMPTY at A·1: the three
// kid usage cards land at A·2, the two powers (reset / unlock) at A·3. esbuild bundles
// this to public/app.js. No AI, no gamification — this is a utility console.
import { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";

// --- tiny API client (same-origin; the session cookie rides along via credentials:include) ---
const JSON_HEADERS = { "content-type": "application/json" };

async function req(method, path, body) {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  return { ok: res.ok, status: res.status, data };
}

const api = {
  async me() {
    try {
      const r = await req("GET", "/api/me");
      return r.ok && r.data ? r.data.user : null;
    } catch {
      return null; // no network / not signed in -> treat as signed out
    }
  },
  signIn: (email, password) => req("POST", "/api/login", { email, password }),
  createAccount: (email, password) => req("POST", "/api/register", { email, password }),
  async signOut() { try { await req("POST", "/api/logout"); } catch { /* ignore */ } },

  // A·2: the read-only usage sweep — one call, all three kids.
  async overview() {
    const r = await req("GET", "/api/admin/overview");
    return r.ok && r.data && Array.isArray(r.data.apps) ? r.data.apps : null;
  },

  // A·3: the two powers — the only writes admin ever makes against a kid's DB, each
  // audited into the target app's own audit log server-side.
  resetPassword: (app, email, newPassword) => req("POST", "/api/admin/reset-password", { app, email, newPassword }),
  deleteUser: (app, email, confirmEmail) => req("POST", "/api/admin/delete-user", { app, email, confirmEmail }),

  // M2·1: backup freshness — null when the endpoint is unreachable (the panel stays quiet).
  async backups() {
    try {
      const r = await req("GET", "/api/admin/backups");
      return r.ok && r.data && Array.isArray(r.data.apps) ? r.data : null;
    } catch { return null; }
  },

  // J·3: the card reports panel — read each kid's open reports; resolve one (audited into the target's log).
  async reports() {
    const r = await req("GET", "/api/admin/reports");
    return r.ok && r.data && Array.isArray(r.data.apps) ? r.data.apps : null;
  },
  resolveReport: (app, reportId) => req("POST", "/api/admin/reports/resolve", { app, reportId }),

  // BIG·5c·1: the chapter-job ledger — read-only; null when unreachable (the panel stays quiet).
  async jobs() {
    try {
      const r = await req("GET", "/api/admin/jobs");
      return r.ok && r.data && Array.isArray(r.data.apps) ? r.data.apps : null;
    } catch { return null; }
  },
};

// --- the one signature element: the wordmark + ADMIN chip (styled in index.html) ---
function Wordmark() {
  return <span className="wordmark">School Apps<span className="chip" aria-hidden="true">ADMIN</span></span>;
}

// F·2: map an auth failure to plain console copy by STATUS (not fragile string-matching), preferring
// the server's own text on a 400 (it carries the real validation bound — e.g. the password length).
// A thrown fetch is handled at the call site as "couldn't reach the server". Admin has no kid register
// — its 403 is the single-account console lock.
function authError(registering, r) {
  const server = r.data && typeof r.data.error === "string" ? r.data.error.trim() : "";
  // F2·1: prefer the server text on 429 too — it carries the honest lockout minutes (S2's contract);
  // without this the lockout rendered the generic line and read as a silent dead-end in the field.
  if ((r.status === 400 || r.status === 429) && server) return server;
  if (r.status === 429) return "Too many attempts — wait a few minutes and try again.";
  if (registering) {
    if (r.status === 409) return "That email already has an account — sign in instead.";
    if (r.status === 403) return "This console already has its account — registration is closed.";
  } else if (r.status === 401) {
    return "Incorrect email or password.";
  }
  return "That didn't work — check your details and try again.";
}

function AuthScreen({ onSignedIn }) {
  const [mode, setMode] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const registering = mode === "register";

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) { setError("Enter an email and a password."); return; }
    setBusy(true);
    let r;
    try {
      r = registering
        ? await api.createAccount(email.trim(), password)
        : await api.signIn(email.trim(), password);
    } catch {
      setBusy(false);
      setError("Couldn't reach the server — nothing changed. Check your connection and retry.");
      return;
    }
    setBusy(false);
    if (r.ok && r.data) { onSignedIn(r.data.user); return; }
    setError(authError(registering, r));
  }

  function swap(next) { setMode(next); setError(""); }

  return (
    <main className="auth">
      <form className="card" onSubmit={submit} noValidate>
        <Wordmark />
        <p className="tagline">Oversight for the family school apps.</p>

        <label className="field">
          <span>Email</span>
          <input type="email" autoComplete="email" inputMode="email"
            value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" autoComplete={registering ? "new-password" : "current-password"}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Just a moment…" : registering ? "Create account" : "Sign in"}
        </button>

        <p className="switch">
          {registering
            ? <>Already set up? <button type="button" className="link" onClick={() => swap("signin")}>Sign in</button></>
            : <>First run? <button type="button" className="link" onClick={() => swap("register")}>Create the account</button></>}
        </p>
      </form>
    </main>
  );
}

// --- A·2: the usage dashboard — three read-only kid cards -----------------------------------
const APP_META = {
  alivia: { label: "Alivia", sub: "alivia.school-app.net" },
  estelle: { label: "Estelle", sub: "estelle.school-app.net" },
  dylan: { label: "Dylan", sub: "dylan.school-app.net" },
};

function fmtBytes(n) {
  if (typeof n !== "number") return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// O·1 — whole minutes → "Xh Ym" / "Xh" / "Ym" (hour logs are per-category totals).
function fmtMins(m) {
  if (typeof m !== "number" || m <= 0) return "0m";
  const h = Math.floor(m / 60), min = Math.round(m % 60);
  return h > 0 ? (min > 0 ? `${h}h ${min}m` : `${h}h`) : `${min}m`;
}
function fmtWhen(iso) {
  if (!iso) return "no activity yet";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function Row({ label, children }) {
  return (
    <div className="kid-row">
      <span className="kid-label">{label}</span>
      <span className="kid-value">{children}</span>
    </div>
  );
}

// A·3 — the two power forms on a provisioned card with an account. Buttons are explicit
// clicks (no implicit form submit); every outcome lands as unmistakable text on the card.
function Powers({ s, label, setMsg, onChanged }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const email = s.account.email;

  async function reset() {
    if (!pw || busy) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.resetPassword(s.app, email, pw);
      if (r.ok) { setPw(""); setMsg({ kind: "ok", text: `Password reset for ${email}.` }); }
      else setMsg({ kind: "err", text: r.data?.error ? `Reset failed — ${r.data.error}.` : "Reset failed — try again." });
    } catch {
      setMsg({ kind: "err", text: "Couldn't reach the server — nothing was changed." });
    } finally { setBusy(false); }
  }

  async function remove() {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.deleteUser(s.app, email, confirm);
      if (r.ok) {
        setConfirm("");
        setMsg({ kind: "ok", text: `Account removed — registration on ${label} is open again.` });
        onChanged(); // refresh the overview: the card now shows "no account yet"
      } else {
        setMsg({ kind: "err", text: r.data?.error ? `Nothing changed — ${r.data.error}.` : "Nothing changed — try again." });
      }
    } catch {
      setMsg({ kind: "err", text: "Couldn't reach the server — nothing was changed." });
    } finally { setBusy(false); }
  }

  return (
    <div className="powers">
      <div className="power">
        <span className="power-name">Reset password</span>
        <div className="power-form">
          <input type="password" placeholder="New password" autoComplete="new-password"
            value={pw} onChange={(e) => setPw(e.target.value)} />
          <button type="button" className="btn btn-power" disabled={busy || !pw} onClick={reset}>Reset password</button>
        </div>
      </div>
      <div className="power">
        <span className="power-name">Unlock registration / remove account</span>
        {/* XR·1b — state plainly what this does and does NOT remove, and the inheritance risk. */}
        <p className="power-warn">Removes the login for {email} and re-opens registration on {label}.
          It does <b>NOT</b> delete study data — cards, schedule, review history, hours and courses all
          remain. <b>The next person to register on {label} inherits that existing data.</b> To wipe the
          study data, run <code>scripts/wipe-app-data.js {s.app}</code> on the droplet (takes a backup
          first; needs --force + a typed token).</p>
        <div className="power-form">
          <input type="text" placeholder="Type the email to confirm" autoComplete="off"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <button type="button" className="btn btn-power btn-danger" disabled={busy || !confirm} onClick={remove}>Remove account</button>
        </div>
      </div>
    </div>
  );
}

function KidCard({ s, onChanged }) {
  const meta = APP_META[s.app] ?? { label: s.app, sub: "" };
  const [msg, setMsg] = useState(null); // { kind: "ok" | "err", text } — survives the re-render after a delete
  if (!s.provisioned) {
    return (
      <section className="kid-card">
        <header className="kid-head">
          <h2>{meta.label}</h2>
          <span className="kid-sub">{meta.sub}</span>
        </header>
        <p className="muted">Not provisioned yet — no database on this machine.</p>
      </section>
    );
  }
  return (
    <section className="kid-card">
      <header className="kid-head">
        <h2>{meta.label}</h2>
        <span className="kid-sub">{meta.sub}</span>
      </header>
      <Row label="Account">
        {s.account
          ? <>{s.account.email} <span className="kid-dim">since {fmtWhen(s.account.createdAt).split(",")[0]}</span></>
          : <>no account yet <span className="kid-dim">— registration is open</span></>}
      </Row>
      <Row label="Last activity">{fmtWhen(s.lastActivity)}</Row>
      <Row label="Courses">{s.courses}</Row>
      <Row label="Assignments">
        {s.assignments.open} open{s.assignments.overdue > 0 && <span className="kid-warn"> · {s.assignments.overdue} overdue</span>}
      </Row>
      <Row label="Cards">{s.cards}</Row>
      {s.srs && (
        <Row label="Reviews">
          {s.srs.dueNow} due now <span className="kid-dim">· {s.srs.reviewedToday} today · {s.srs.reviewed7d} last 7 days</span>
        </Row>
      )}
      {/* O·1 — the L·2 learning-state split (girls only) */}
      {s.learning && (
        <Row label="Learning">
          {s.learning.learning} learning <span className="kid-dim">· {s.learning.probation} probation · {s.learning.review} review</span>
        </Row>
      )}
      {s.anki && (
        <Row label="Anki">
          {s.anki.pending} pending export <span className="kid-dim">· {s.anki.exported} exported</span>
        </Row>
      )}
      {/* O·1 — hours logged in the last 30 days, per category */}
      {s.hours30d && (
        <Row label="Hours (30d)">
          {Object.keys(s.hours30d).length === 0
            ? <span className="kid-dim">none logged</span>
            : Object.keys(s.hours30d).sort().map((cat, i) => (
                <span key={cat}>{i > 0 && <span className="kid-dim"> · </span>}{cat} {fmtMins(s.hours30d[cat])}</span>
              ))}
        </Row>
      )}
      {/* O·2 — AI tutor activity (from audit events: ids/counts only, no content exists to show) */}
      {s.tutor && (
        <Row label="AI Tutor">
          {s.tutor.turns30d === 0
            ? <span className="kid-dim">no tutor use yet</span>
            : <>{s.tutor.turns7d} <span className="kid-dim">turns (7d)</span> · {s.tutor.turns30d} <span className="kid-dim">(30d) · {s.tutor.decks} deck{s.tutor.decks === 1 ? "" : "s"}
                {s.tutor.fellBack > 0 && ` · ${s.tutor.fellBack} fell back`}{s.tutor.deferred > 0 && ` · ${s.tutor.deferred} deferred`}</span></>}
        </Row>
      )}
      {/* O·1 — calendar subscription feed status (boolean only — never the token) */}
      {s.calendarFeed && (
        <Row label="Calendar feed">
          {s.calendarFeed.minted ? "minted" : <span className="kid-dim">not set up</span>}
        </Row>
      )}
      <Row label="Database">{fmtBytes(s.dbBytes)}</Row>
      {s.account && <Powers s={s} label={meta.label} setMsg={setMsg} onChanged={onChanged} />}
      {msg && (
        <p className={msg.kind === "ok" ? "power-ok" : "error"} role={msg.kind === "ok" ? "status" : "alert"}>
          {msg.text}
        </p>
      )}
    </section>
  );
}

function Dashboard() {
  const [apps, setApps] = useState(null);  // null loading | stats[]
  const [failed, setFailed] = useState(false);
  function reload() {
    return api.overview().then((a) => { if (a) setApps(a); else setFailed(true); });
  }
  useEffect(() => { reload(); }, []);
  return (
    <div className="dash">
      <header className="dash-head">
        <h1>Family apps</h1>
        <p className="dash-sub">Usage at a glance; reset a password or re-open registration when a kid is locked out.</p>
      </header>
      {failed
        ? <p className="error" role="alert">Couldn't load the overview — refresh to retry.</p>
        : apps == null
          ? <p className="muted">Loading…</p>
          : <div className="kids">{apps.map((s) => <KidCard key={s.app} s={s} onChanged={reload} />)}</div>}
      <BackupsPanel />
      <CardReportsPanel />
      <ChapterJobsPanel />
    </div>
  );
}

// BIG·5c·1 — the chapter-job LEDGER: per kid, the recent chapter jobs as the STORE reads them.
// Visibility, zero power: no abort, no gate, no buttons — the kid owns those. Every cell is a text
// node. The status word is the store's (including its derived "stalled"); a job at the consent
// gate is labelled as waiting, because the store never calls that stalled and neither does this.
function jobStatusLabel(j) {
  if (j.awaitingConsent) return "waiting at the gate";
  return j.status;
}
function fmtStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
function ChapterJobsPanel() {
  const [apps, setApps] = useState(null);
  useEffect(() => { api.jobs().then((a) => setApps(a ?? [])); }, []);
  if (apps == null) return null;
  return (
    <section className="jobs-panel">
      <h2>Chapter jobs</h2>
      <p className="muted">Recent chapter runs per kid, as their own tracker sees them. Visibility only — the gate and the Stop button are theirs.</p>
      {apps.map((a) => {
        const meta = APP_META[a.app] ?? { label: a.app };
        return (
          <div key={a.app} className="jobs-kid">
            <h3>{meta.label}</h3>
            {!a.provisioned
              ? <p className="muted">Not provisioned yet — no database on this machine.</p>
              : a.jobs.length === 0
                ? <p className="muted jobs-empty">No chapter jobs yet.</p>
                : (
                  <div className="jobs-scroll">
                    <table className="jobs-table">
                      <thead>
                        <tr><th>Chapter</th><th>Status</th><th>Sections</th><th>Cards</th><th>Tokens</th><th>Started</th><th>Finished</th></tr>
                      </thead>
                      <tbody>
                        {a.jobs.map((j) => (
                          <tr key={j.id} className={`job-row job-${j.awaitingConsent ? "gated" : j.status}`}>
                            <td className="job-source">
                              {j.source
                                ? <>{j.source.filename} <span className="kid-dim">{j.source.size}</span></>
                                : <span className="kid-dim">{j.libraryId ? "(source deleted)" : "(no source)"}</span>}
                            </td>
                            <td className="job-status">
                              <span className={j.status === "stalled" ? "kid-warn" : undefined}>{jobStatusLabel(j)}</span>
                              {j.error && <span className="job-error"> — {j.error}</span>}
                            </td>
                            <td className="job-segments">
                              {j.segments.done}/{j.segments.total}
                              {j.segments.failed > 0 && <span className="kid-warn"> · {j.segments.failed} failed</span>}
                            </td>
                            <td className="job-cards">
                              {/* landed = what a sink counted; null means nothing ever counted — shown as "—", never 0 */}
                              {j.cardsLanded == null ? "—" : j.cardsLanded}
                              <span className="kid-dim"> of {j.cardsMinted} made</span>
                              {j.cardsLost > 0 && <span className="kid-warn"> · {j.cardsLost} lost</span>}
                            </td>
                            <td className="job-tokens">
                              {/* CST·1 — tokens only, never dollars; "—" where nothing was measured */}
                              {j.tokens == null ? "—" : j.tokens}
                              {/* CST·2 — the cached share, only when the counters exist */}
                              {j.tokensCached != null && <span className="kid-dim"> ({j.tokensCached} cached)</span>}
                              <span className="kid-dim"> · {j.tokensPerCard == null ? "—" : j.tokensPerCard} per card</span>
                            </td>
                            <td className="job-when">{fmtStamp(j.startedAt)}</td>
                            <td className="job-when">{fmtStamp(j.finishedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
          </div>
        );
      })}
    </section>
  );
}

// M2·1/M2·2 — the Backups card: newest-backup age per app (green under 26h, amber under 50h, red
// beyond or unreadable), the cron log's last line when readable, and disk free for the data volume.
// No Drive API — local staleness is the honest proxy. A dev machine (no dir, no log) degrades to
// one honest line, never a red wall.
function BackupsPanel() {
  const [b, setB] = useState(null);
  useEffect(() => { api.backups().then(setB); }, []);
  if (b === null) return null; // loading or endpoint unreachable — the dashboard stays quiet
  return (
    <section className="backups-panel">
      <h2>Backups</h2>
      {!b.dirExists
        ? <p className="muted">No backups directory on this machine — freshness reads live on the droplet.</p>
        : (
          <ul className="backup-rows">
            {(b.apps ?? []).map((a) => (
              <li key={a.app} className={`backup-row backup-${a.status}`}>
                <span className="backup-dot" aria-hidden="true" />
                <span className="backup-app">{a.app}</span>
                <span className="backup-age">
                  {a.ageHours == null ? (a.reason ?? "no backup") : a.ageHours < 48 ? `${a.ageHours}h ago` : `${Math.round(a.ageHours / 24 * 10) / 10}d ago`}
                </span>
              </li>
            ))}
          </ul>
        )}
      {b.disk && <p className="muted">Data volume: {fmtBytes(b.disk.freeBytes)} free of {fmtBytes(b.disk.totalBytes)}.</p>}
      {b.log?.line && <p className="backup-log" title="last line of the backup cron log">{b.log.line}</p>}
    </section>
  );
}

// J·3 — the Card reports panel: per-kid open reports (front/back + reason + age), each with a
// "Mark resolved" action (audited into that kid's own log server-side). Honest empty state.
function CardReportsPanel() {
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(null); // the reportId currently resolving
  function reload() { return api.reports().then((a) => setApps(a ?? [])); }
  useEffect(() => { reload(); }, []);

  async function resolve(app, reportId) {
    setBusy(reportId);
    try { const r = await api.resolveReport(app, reportId); if (r.ok) await reload(); }
    finally { setBusy(null); }
  }

  if (apps == null) return null;
  const withReports = apps.filter((a) => a.reports.length > 0);
  const total = withReports.reduce((n, a) => n + a.reports.length, 0);

  return (
    <section className="reports-panel">
      <h2>Card reports{total > 0 ? ` (${total})` : ""}</h2>
      {total === 0
        ? <p className="muted">No open card reports — nothing to look at.</p>
        : withReports.map((a) => (
            <div key={a.app} className="reports-kid">
              <h3>{a.app}</h3>
              <ul className="report-list">
                {a.reports.map((r) => (
                  <li key={r.id} className="report-item">
                    <div className="report-card">
                      <p className="report-front">{r.front ?? "(card not found)"}</p>
                      {/* VAR·4 — the phrasing she actually saw, ONLY when it differs from the canonical
                          front (a variant was showing); identical or absent renders exactly as before */}
                      {r.shownFront && r.shownFront !== r.front && <p className="report-shown">Shown as: “{r.shownFront}”</p>}
                      {r.back && <p className="report-back">{r.back}</p>}
                    </div>
                    {r.reason && <p className="report-reason">“{r.reason}”</p>}
                    {/* MR·2 — the leech-repair suggestion, READ-ONLY. No apply buttons, no writes, no
                        AI here (admin imports nothing from core/ai): the fix is run by a droplet script. */}
                    {r.suggestion && (
                      <div className="repair-suggestion">
                        <p className="repair-diagnosis"><span className="repair-label">Suggested repair:</span> {r.suggestion.diagnosis}</p>
                        {r.suggestion.cardIsFine
                          ? <p className="repair-fine">✓ Card is well-formed — the material is just hard. No rewrite proposed.</p>
                          : <>
                              {Array.isArray(r.suggestion.split) && r.suggestion.split.length > 0 && (
                                <div className="repair-split">
                                  <p className="repair-sub">Split into {r.suggestion.split.length} atomic cards:</p>
                                  <ul>{r.suggestion.split.map((c, i) => <li key={i}><b>{c.front}</b> — {c.back}</li>)}</ul>
                                </div>
                              )}
                              {r.suggestion.rewrite && (
                                <p className="repair-rewrite">Rewrite → <b>{r.suggestion.rewrite.front}</b> — {r.suggestion.rewrite.back}</p>
                              )}
                            </>}
                        {r.suggestion.mnemonic && <p className="repair-mnemonic">Mnemonic: {r.suggestion.mnemonic}</p>}
                        <p className="repair-meta">Run <code>apply-repair.js {r.id}</code> to apply · suggested by {r.suggestion.suggestedBy ?? "the model"}</p>
                      </div>
                    )}
                    <div className="report-foot">
                      {/* LX·2 — who filed it: the kid's own report vs the automatic leech scan */}
                      <span className={`report-origin origin-${r.origin ?? "kid"}`}>{r.origin === "leech" ? "leech-detected" : "kid-reported"}</span>
                      <span className="report-age">{r.ageDays === 0 ? "today" : r.ageDays === 1 ? "1 day ago" : `${r.ageDays} days ago`}</span>
                      <button className="btn btn-sm" disabled={busy === r.id} onClick={() => resolve(a.app, r.id)}>Mark resolved</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
    </section>
  );
}

function Shell({ user, onSignOut }) {
  return (
    <div className="app">
      <header className="topbar">
        <Wordmark />
        <div className="topbar-right">
          <span className="who">{user.email}</span>
          <button className="link" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main className="content">
        <Dashboard />
      </main>
      {/* V·1: the build baked into THIS bundle (esbuild define from sw.js VERSION) — a stale install shows the old string. */}
      <footer className="app-footer">{__APP_VERSION__}</footer>
    </div>
  );
}

function App() {
  const [status, setStatus] = useState("checking"); // "checking" | "out" | "in"
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    api.me().then((u) => {
      if (!alive) return;
      if (u) { setUser(u); setStatus("in"); } else setStatus("out");
    });
    return () => { alive = false; };
  }, []);

  async function signOut() {
    await api.signOut();
    setUser(null);
    setStatus("out");
  }

  if (status === "checking") return <main className="splash"><Wordmark /></main>;
  if (status === "in") return <Shell user={user} onSignOut={signOut} />;
  return <AuthScreen onSignedIn={(u) => { setUser(u); setStatus("in"); }} />;
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<App />);

// Register the service worker (offline shell + installability). Guarded so it no-ops in
// environments without it (e.g. the jsdom smoke test).
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
